import { apiFetch } from "@/lib/api-client";

export type BillingInterval = "month" | "year";

export type PlanFeatures = {
	scheduledFlows?: boolean;
	analytics?: "basic" | "full";
	ads?: boolean;
	approvals?: boolean;
	assistant?: boolean;
	whiteLabelReports?: boolean;
	competitors?: number;
};

export type Plan = {
	id: string;
	name: string;
	description: string;
	monthlyPriceCents: number;
	yearlyPriceCents: number;
	includedMillicredits: number;
	rolloverMillicredits: number;
	seats: number;
	extraSeatPriceCents: number | null;
	maxProjects: number | null;
	maxSocialAccounts: number | null;
	maxFlows: number | null;
	maxSchedules: number | null;
	maxCompetitors: number | null;
	trialDays: number;
	features: PlanFeatures;
};

export type CreditPack = { id: string; name: string; millicredits: number; priceCents: number };

export type BillingCatalog = {
	plans: Plan[];
	packs: CreditPack[];
	rates: {
		usdPerCredit: number;
		millicreditsPerCredit: number;
		markup: Record<string, number>;
		flatFeesMillicredits: Record<string, number>;
		storageFreeGb: number;
		storageMillicreditsPerGbMonth: number;
		trialCreditShare: number;
	};
};

export type LimitKey =
	| "maxProjects"
	| "maxFlows"
	| "maxSocialAccounts"
	| "maxSchedules"
	| "maxCompetitors"
	| "seats";

export type BillingSummary = {
	configured: boolean;
	plan: Plan | null;
	balanceMillicredits: number;
	subscription: {
		planId: string;
		status: string;
		interval: BillingInterval;
		seats: number;
		extraSeats: number;
		currentPeriodStart: string | null;
		currentPeriodEnd: string | null;
		cancelAtPeriodEnd: boolean;
		trialEndsAt: string | null;
		managedByStripe: boolean;
	} | null;
	hasStripeCustomer: boolean;
	credits: {
		balanceMillicredits: number;
		includedMillicredits: number;
		packMillicredits: number;
		spendableMillicredits: number;
		paymentPastDue: boolean;
		lowBalanceThresholdMillicredits: number;
		autoTopUp: {
			enabled: boolean;
			thresholdMillicredits: number;
			packId: string | null;
			pending: boolean;
			error: string | null;
		};
	};
	usage: Record<LimitKey, { used: number; limit: number | null }> | null;
};

export type UsageSource = "openrouter" | "brightdata" | "zernio" | "storage" | "other";

export type UsageGroup = {
	key: string;
	source: UsageSource;
	events: number;
	millicredits: number;
	providerCostUsd: number;
};

export type UsageSummary = {
	from: string;
	to: string;
	totals: { events: number; millicredits: number; providerCostUsd: number };
	bySource: UsageGroup[];
	byFeature: UsageGroup[];
	byModel: UsageGroup[];
	byProject: (Omit<UsageGroup, "source"> & { projectId: string | null })[];
	daily: { date: string; millicredits: number }[];
};

export type LedgerReason =
	| "plan_grant"
	| "pack_purchase"
	| "usage"
	| "refund"
	| "adjustment"
	| "expiration"
	| "promo";

export type LedgerEntry = {
	id: string;
	createdAt: string;
	amountMillicredits: number;
	balanceAfterMillicredits: number;
	reason: LedgerReason;
	description: string;
	source: UsageSource | null;
	feature: string | null;
	model: string | null;
	projectId: string | null;
	providerCostUsd: number | null;
};

export type LedgerPage = { entries: LedgerEntry[]; nextCursor: string | null };

export type Invoice = {
	id: string;
	number: string | null;
	status: string | null;
	totalCents: number;
	currency: string;
	createdAt: string;
	description: string | null;
	hostedUrl: string | null;
	pdfUrl: string | null;
};

export type PortalFlow = "home" | "payment_method_update" | "subscription_cancel";

const post = <T>(path: string, body: unknown, method = "POST") =>
	apiFetch<T>(path, { method, body: JSON.stringify(body) });

export const getBillingSummary = () => apiFetch<BillingSummary>("/billing/summary");
export const getBillingCatalog = () => apiFetch<BillingCatalog>("/billing/catalog");
export const getUsageSummary = (range: { from: string; to: string }) =>
	apiFetch<UsageSummary>(
		`/billing/usage?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
	);
export const getLedgerPage = (cursor: string | null, limit = 100) =>
	apiFetch<LedgerPage>(
		`/billing/ledger?limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
	);
export const getInvoices = () =>
	apiFetch<{ configured: boolean; invoices: Invoice[] }>("/billing/invoices");

export const startCheckout = (input: {
	planId: string;
	interval: BillingInterval;
	extraSeats?: number;
}) => post<{ url: string }>("/billing/checkout", input);
export const startCreditCheckout = (input: { packId: string; saveCard?: boolean }) =>
	post<{ url: string }>("/billing/checkout/credits", input);
export const confirmCheckout = (sessionId: string) =>
	post<{ status: string | null; mode: string; outcome: string }>("/billing/checkout/confirm", {
		sessionId,
	});
export const changePlan = (input: { planId: string; interval: BillingInterval }) =>
	post<{ url: string }>("/billing/change-plan", input);
export const openPortal = (flow: PortalFlow = "home") =>
	post<{ url: string }>("/billing/portal", { flow });
export const updateAutoTopUp = (input: {
	enabled: boolean;
	thresholdCredits: number;
	packId: string;
}) =>
	post<{ enabled: boolean; thresholdMillicredits: number; packId: string }>(
		"/billing/auto-top-up",
		input,
		"PUT",
	);
export const updateSeats = (extraSeats: number) =>
	post<{ seats: number }>("/billing/seats", { extraSeats }, "PUT");
